import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { useRecommendedTemplates } from "@/components/confluence/templates/context/recommended-templates-context.tsx";
import { useTemplateFuzzySearchContext } from "@/components/confluence/templates/context/template-fuzzy-search-context";
import { getIcons } from "@/components/confluence/templates/get-icons.tsx";
import { Tile } from "@/components/tile/src";
import { Category } from "@/content/templates/template.types.ts";
import {
    CategoryKey,
    CategoryOrder
} from "@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order.ts";
import { withFeatureExposed } from "@/utils/feature-exposed";

const categoriesSearchContainer = css({
    display: "flex",
    width: "100%",
    gap: 16
});

const categoriesContainer = css({
    flex: "1 1 0px",
    minWidth: "0%", // this is needed to constrain the children within the element box
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    padding: "2px"
});

const getCategoryIndex = (
    urlSlug: string,
    orderMap: { [key: string]: number }
): number => {
    return orderMap[urlSlug as keyof typeof orderMap] ?? Number.MAX_VALUE;
};

const sortCategories = (
    categories: Category[],
    orderMap: { [key: string]: number }
): Category[] => {
    return categories.sort((a, b) => {
        const indexA = a.urlSlug
            ? getCategoryIndex(a.urlSlug, orderMap)
            : Number.MAX_VALUE;
        const indexB = b.urlSlug
            ? getCategoryIndex(b.urlSlug, orderMap)
            : Number.MAX_VALUE;
        return indexA - indexB;
    });
};

const TemplateCategoriesUnifiedSearchComponent = () => {
    const { adjustedCategories: categories } = useRecommendedTemplates();
    const { currentCategory, setCategory } = useTemplateFuzzySearchContext();
    const [sortedCategories, setSortedCategories] = useState<Category[]>(
        sortCategories(
            categories.filter(
                (category) =>
                    category.urlSlug === "recommended" ||
                    category.urlSlug === "project-management" ||
                    category.urlSlug === "software-development-and-it" ||
                    category.urlSlug === "product-management" ||
                    category.urlSlug === "marketing-and-sales" ||
                    category.urlSlug === "design" ||
                    category.urlSlug === "finance-and-operations" ||
                    category.urlSlug === "human-resources"
            ),
            CategoryOrder
        )
    );
    const intl = useIntl();
    const locale = intl.locale;

    const localizedLinkForCategory = (categoryId: string) => {
        const prefix = locale === "en" ? "" : `/${locale}`;
        if (categoryId === "recommended") {
            return `${prefix}/software/confluence/templates`;
        }
        return `${prefix}/software/confluence/templates/categories/${categoryId}`;
    };

    useEffect(() => {
        const categoriesToDisplay = categories.filter(
            (category) =>
                category.urlSlug === "recommended" ||
                category.urlSlug === "project-management" ||
                category.urlSlug === "software-development-and-it" ||
                category.urlSlug === "product-management" ||
                category.urlSlug === "marketing-and-sales" ||
                category.urlSlug === "design" ||
                category.urlSlug === "finance-and-operations" ||
                category.urlSlug === "human-resources"
        );

        setSortedCategories(sortCategories(categoriesToDisplay, CategoryOrder));
    }, [categories]);

    return (
        <div
            css={categoriesSearchContainer}
            data-testid="confluence-template-categories-unified-search"
        >
            <div css={css({ margin: "0 auto" })}>
                <Adg4Tabs>
                    <Adg4DraggableTabList>
                        <div
                            css={categoriesContainer}
                            data-testid={"category-list"}
                        >
                            {sortedCategories?.map((category) => (
                                <Adg4DraggableTab key={category.urlSlug}>
                                    <Tile
                                        href={localizedLinkForCategory(
                                            category?.urlSlug ?? ""
                                        )}
                                        key={category.urlSlug}
                                        label={category.name}
                                        onClick={() => setCategory(category)}
                                        isActive={
                                            category.urlSlug ===
                                            currentCategory?.urlSlug
                                        }
                                        {...getIcons(
                                            category.urlSlug as CategoryKey
                                        )}
                                    />
                                </Adg4DraggableTab>
                            ))}
                        </div>
                    </Adg4DraggableTabList>
                </Adg4Tabs>
            </div>
        </div>
    );
};

export const TemplateCategoriesUnifiedSearch = withFeatureExposed(
    "cfind-1795-refresh-template-categories",
    "enrolled",
    TemplateCategoriesUnifiedSearchComponent
);
